import Navbar from "@/components/marketing/Navbar";
import Footer from "@/components/marketing/Footer";
import { SeoHead } from "@/components/resources/SeoHead";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Linkedin } from "lucide-react";

const META = {
  title: "About NetLift — evidence-based AI investment decisions",
  description: "NetLift helps organisations measure the cost, savings and return of AI adoption — so they can see where AI is creating value, where it isn't, and what to do next.",
  robots: "index,follow",
  canonical: "https://netlift.app/about",
  og_title: "About NetLift",
  og_description: "AI investment decisions should be based on evidence. NetLift measures the cost, savings and return of AI adoption.",
  og_url: "https://netlift.app/about",
};

const QUESTIONS = [
  "Where is AI actually saving time?",
  "What is that saving worth?",
  "What does the AI cost?",
  "How strong is the evidence behind the result?",
  "What should we expand, improve, review or stop?",
];

const APPROACH = [
  {
    title: "Measure before you claim.",
    body: "NetLift compares AI-assisted work with a baseline for how that work was completed without AI. Savings should be measured, not assumed.",
  },
  {
    title: "Separate savings from capacity.",
    body: "Time saved does not automatically equal money saved. NetLift distinguishes between financial savings, capacity created and projected value so different types of return are not treated as the same thing.",
  },
  {
    title: "Show the strength of the evidence.",
    body: "Not every ROI result deserves the same level of confidence. NetLift helps decision-makers understand the quality of the evidence behind the number.",
  },
  {
    title: "Measure work, not people.",
    body: "NetLift is designed to support AI investment decisions, not employee surveillance. No screenshots, keystroke monitoring or individual productivity scoring.",
  },
];

const CLOSING_LINES = ["Measure the work.", "See the evidence.", "Show the return.", "Make the decision."];

export default function About() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <SeoHead meta={META} />
      <Navbar />
      <main className="mx-auto max-w-3xl px-6 py-16 sm:py-20">
        {/* Section 1 — Hero */}
        <section data-testid="about-hero">
          <span className="text-xs font-semibold uppercase tracking-wide text-editorial">About NetLift</span>
          <h1 className="mt-2 font-heading text-4xl font-semibold leading-tight text-navy sm:text-5xl">
            AI investment decisions should be based on evidence.
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">
            NetLift helps organisations measure the cost, savings and return of AI adoption — so they
            can see where AI is creating value, where it isn&rsquo;t, and what to do next.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            Because AI usage is not the same thing as AI value.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button className="bg-editorial hover:bg-editorial/90" onClick={() => navigate("/signup")} data-testid="about-hero-trial">Start Free Trial</Button>
            <Button variant="outline" onClick={() => navigate("/demo")} data-testid="about-hero-demo">See a Sample Report</Button>
          </div>
        </section>

        {/* Section 2 — Why NetLift exists */}
        <section className="mt-16 sm:mt-20" data-testid="about-why">
          <h2 className="font-heading text-2xl font-semibold text-navy sm:text-3xl">
            AI is getting easier to adopt. Proving its value isn&rsquo;t.
          </h2>
          <p className="mt-5 leading-relaxed text-slate-600">
            Businesses can see how many AI licences they have, how often tools are being used and how
            much AI activity is happening.
          </p>
          <p className="mt-3 leading-relaxed text-slate-600">
            Those numbers can show adoption. They don&rsquo;t necessarily show return.
          </p>
          <p className="mt-6 font-medium text-navy">NetLift was built to help answer the questions that come next:</p>
          <ul className="mt-4 space-y-2.5">
            {QUESTIONS.map((q) => (
              <li key={q} className="flex gap-3 text-slate-600">
                <span className="mt-2.5 h-1 w-4 shrink-0 rounded-full bg-editorial/50" aria-hidden="true" />
                <span className="leading-relaxed">{q}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 leading-relaxed text-slate-600">
            NetLift brings those questions together in one place, helping teams move from measuring AI
            activity to making better AI investment decisions.
          </p>
        </section>

        {/* Section 3 — Our approach */}
        <section className="mt-16 sm:mt-20" data-testid="about-approach">
          <h2 className="font-heading text-2xl font-semibold text-navy sm:text-3xl">
            A more disciplined approach to AI ROI.
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {APPROACH.map(({ title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6">
                <p className="font-heading text-lg font-semibold text-navy">{title}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 4 — Founder */}
        <section className="mt-16 sm:mt-20" data-testid="about-founder">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
            <div className="flex flex-col gap-6 sm:flex-row sm:gap-8">
              <img
                src="/founder-paige.jpg"
                alt="Paige Gilmore, founder of NetLift"
                className="h-44 w-44 shrink-0 rounded-xl object-cover object-top"
                data-testid="about-founder-photo"
              />
              <div className="min-w-0">
                <h2 className="font-heading text-2xl font-semibold text-navy">
                  Founded on a background in ROI analysis.
                </h2>
                <p className="mt-4 leading-relaxed text-slate-600">
                  NetLift was founded by <strong>Paige Gilmore</strong>, a seasoned commercial analyst
                  and business operator with more than a decade of experience evaluating ROI and
                  commercial performance.
                </p>
                <p className="mt-3 leading-relaxed text-slate-600">
                  Her work has spanned investment decisions ranging from outbound call centre
                  performance and marketing spend to customer acquisition, ecommerce and broader
                  commercial growth.
                </p>
                <p className="mt-3 leading-relaxed text-slate-600">
                  Throughout that work, the underlying question has remained the same:{" "}
                  <em>&ldquo;What are we spending, what are we getting back, and is it worth continuing?&rdquo;</em>
                </p>
                <p className="mt-3 leading-relaxed text-slate-600">
                  NetLift applies that same analytical discipline to AI investment.
                </p>
                <a
                  href="https://www.linkedin.com/in/paige-gilmore-1a2b26104"
                  target="_blank"
                  rel="noreferrer"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-editorial hover:underline"
                  data-testid="about-linkedin-link"
                >
                  <Linkedin className="h-4 w-4" /> Connect with Paige on LinkedIn
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5 — Closing CTA */}
        <section className="mt-16 rounded-2xl bg-navy p-8 text-white sm:mt-20 sm:p-10" data-testid="about-cta">
          <h2 className="font-heading text-2xl font-semibold sm:text-3xl">
            Know what your AI investment is actually producing.
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70">
            Move beyond adoption metrics and understand where AI is creating measurable value across
            your organisation.
          </p>
          <p className="mt-5 font-heading text-sm font-medium tracking-wide text-white/90">
            {CLOSING_LINES.join(" ")}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button className="bg-editorial hover:bg-editorial/90" onClick={() => navigate("/signup")} data-testid="about-cta-trial">Start Free Trial</Button>
            <Button variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" onClick={() => navigate("/demo")} data-testid="about-cta-demo">See a Sample Report</Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
